/* eslint-disable camelcase */

import ReduxService from 'src/redux/redux.service';
import { RootState } from 'src/redux/redux-store';
import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { Panel } from '../types/panel.types';
import { InvisiblyApiResponse } from 'src/types/apiResponses';
import { APITagType } from 'src/redux/api/invisibly-api.types';
import { createTagInvalidationArray } from 'src/redux/api/invisibly-api.helpers';

const getUrl = (url: string) => {
  const state = ReduxService.Store.getState() as RootState;
  const isClientActive = state.userState.activeClient;
  return isClientActive ? url.replace('b2b', 'client') : url;
};

const getClientId = () => {
  const state = ReduxService.Store.getState() as RootState;
  const isClientActive = state.userState.activeClient;
  return isClientActive ? state.userState.activeClient?.ext_client_id ?? null : null;
};


const panelEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    createPanel: builder.mutation<Panel, { name: string; file: File; client?: string; fields?: Array<{ name: string; description: string; active: boolean }> }>({
      query: ({ name, file, client, fields }) => {
        const formData = new FormData();
        formData.append('raw_dataset', file);
        formData.append('name', name);
        if (client) {
          formData.append('client', client);
        }
        if (fields) {
          formData.append('fields', JSON.stringify(fields));
        }
        return {
          method: 'POST',
          url: 'panels',
          data: formData,
        };
      },
      transformResponse(response: InvisiblyApiResponse<Panel>) {
        return response.item;
      },
      invalidatesTags: createTagInvalidationArray(APITagType.Panel),
    }),
    updatePanel: builder.mutation<Panel, { id: string; field: string; value: any }>({
      query: ({ id, field, value }) => ({
        method: 'PATCH',
        url: `panels/${id}`,
        data: { [field]: value },
      }),
      transformResponse(response: InvisiblyApiResponse<Panel>) {
        return response.item;
      },
      invalidatesTags: createTagInvalidationArray(APITagType.Panel),
    }),
    generatePanelists: builder.mutation<Panel, { panel_id: string }>({
      query: ({ panel_id }) => ({
        method: 'POST',
        url: `panels/${panel_id}/generate`,
      }),
      transformResponse(response: InvisiblyApiResponse<Panel>) {
        return response.item;
      },
      invalidatesTags: createTagInvalidationArray(APITagType.Panel),
    }),
    getPanel: builder.query<Panel, { id: string }>({
      query: ({ id }) => ({
        method: 'GET',
        url: getUrl(`panels/${id}`),
      }),
      transformResponse(response: InvisiblyApiResponse<Panel>) {
        return response.item;
      },
    }),
    getPanels: builder.query<Panel[], void>({
      query: () => ({
        method: 'GET',
        url: getUrl('panels'),
        params: {
          client_id: getClientId(),
        },
      }),
      transformResponse(response: InvisiblyApiResponse<Panel[]>) {
        return response.item;
      },
      providesTags: () => [{
        id: 'LIST',
        type: APITagType.Panel,
      }],
    }),
    generateSignedUploadUrl: builder.mutation<string, { panel_id: string, filename: string }>({
      query: ({ panel_id, filename }) => ({
        method: 'POST',
        url: `panels/${panel_id}/signed_url`,
        data: { filename },
      }),
      transformResponse(response: InvisiblyApiResponse<string>) {
        if ('signed_url' in response.item) {
          return response.item.signed_url;
        } else {
          throw new Error('No signed URL received from server');
        }
      },
    }),
    updatePanelField: builder.mutation<Panel, { id: string; field: string; value: any }>({
      query: ({ id, field, value }) => ({
        method: 'PATCH',
        url: getUrl(`panels/fields/${id}`),
        data: { [field]: value },
      }),
      transformResponse(response: InvisiblyApiResponse<Panel>) {
        return response.item;
      },
      invalidatesTags: createTagInvalidationArray(APITagType.Panel),
    }),
  }),
});

export const {
  useCreatePanelMutation,
  useGetPanelsQuery,
  useGetPanelQuery,
  useUpdatePanelMutation,
  useGenerateSignedUploadUrlMutation,
  useGeneratePanelistsMutation,
  useUpdatePanelFieldMutation,
} = panelEndpoints;

export { panelEndpoints };
